import { useState } from 'react';
import InvoiceSearchFilter from '../components/dashboard/InvoiceSearchFilter';
import InvoiceHistoryTable from '../components/dashboard/InvoiceHistoryTable';
import PeriodSelectorBar from '../components/common/PeriodSelectorBar';
import { useInvoices } from '../api/useInvoices';
import { exportInvoices } from '../api/useExportInvoices';
import { usePeriodFilter } from '../context/PeriodFilterContext';

export default function InvoiceListPage() {
  const { dateParams } = usePeriodFilter();
  const [searchVendor, setSearchVendor] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch invoices for date range with all shared records included
  const { data: invoicesData, isLoading: invoicesLoading, isError: invoicesError } = useInvoices({
    start_date: dateParams.start_date,
    end_date: dateParams.end_date,
    vendor: searchVendor || undefined,
    include_shared: true,
  });

  const invoiceItems = invoicesData?.results;

  const handleExport = async () => {
    setIsExporting(true);
    await exportInvoices({
      start_date: dateParams.start_date,
      end_date: dateParams.end_date,
      vendor: searchVendor || undefined,
    });
    setIsExporting(false);
  };

  return (
    <div className="max-w-[1360px] mx-auto py-2 sm:py-4">
      {/* Top Title, Period Filter Bar & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
            / INVOICES & HISTORY
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white tracking-tight mt-0.5">
            Invoice List
          </h1>
          <p className="text-xs sm:text-sm text-brand-muted dark:text-bodydark mt-1">
            Browse, search, and manage all parsed invoices and exported documents.
          </p>
        </div>

        {/* Filter Pills, Date Indicator & Export Action */}
        <PeriodSelectorBar
          showExtraActions={
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white dark:bg-brand-900/40 dark:text-brand-300 dark:hover:bg-brand-vib dark:hover:text-white border border-brand-vib/20 shadow-sm transition-colors cursor-pointer"
            >
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <span>{isExporting ? 'Exporting…' : 'Export CSV'}</span>
            </button>
          }
        />
      </div>

      {/* Natural Language Search Bar */}
      <div className="mb-4">
        <InvoiceSearchFilter onSearch={(q) => setSearchVendor(q)} />
      </div>

      {/* Active Tag / Group Filter Indicator */}
      {selectedTag && (
        <div className="flex items-center gap-2 mb-4 p-2.5 px-4 rounded-xl bg-brand-blue-tint/70 dark:bg-brand-900/30 border border-brand-vib/20 text-xs font-mono">
          <span className="text-brand-soft dark:text-bodydark2">Filtered by:</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md font-semibold bg-brand-vib text-white shadow-xs">
            <span>{selectedTag.startsWith('#') ? selectedTag : `#${selectedTag}`}</span>
            <button
              type="button"
              onClick={() => setSelectedTag(null)}
              className="hover:text-white/80 cursor-pointer font-bold ml-1"
              title="Clear filter"
            >
              ✕
            </button>
          </span>
          <button
            type="button"
            onClick={() => setSelectedTag(null)}
            className="text-xs font-semibold text-brand-vib hover:underline cursor-pointer ml-auto"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Full Invoice History Table */}
      <InvoiceHistoryTable
        invoices={invoiceItems}
        isLoading={invoicesLoading}
        isError={invoicesError}
        onSelectTag={(tag) => setSelectedTag(tag === selectedTag ? null : tag)}
        selectedTag={selectedTag}
      />
    </div>
  );
}
